import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { TopBar } from "../components/TopBar";
import { conditionAnchor, parseAnchor } from "../lib/links";
import { type TaxonomyDoc, loadTaxonomy } from "../sources/taxonomy";
import { useManifestStore } from "../state/manifestStore";
import { CategoriesTab } from "./CategoriesTab";
import { ConditionsTab } from "./ConditionsTab";

const TABS = { CONDITIONS: "conditions", CATEGORIES: "categories" } as const;
type Tab = (typeof TABS)[keyof typeof TABS];

function addToSet<T>(prev: Set<T>, value: T): Set<T> {
  if (prev.has(value)) return prev;
  const next = new Set(prev);
  next.add(value);
  return next;
}

function toggleInSet<T>(prev: Set<T>, value: T): Set<T> {
  const next = new Set(prev);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function Conditions() {
  const { tenant } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const manifest = useManifestStore((s) => s.manifest);

  const [tax, setTax] = useState<TaxonomyDoc | null>(null);
  const [taxError, setTaxError] = useState<string | null>(null);
  useEffect(() => {
    loadTaxonomy().then(setTax, (err) => setTaxError((err as Error).message));
  }, []);

  const parsed = useMemo(() => parseAnchor(location.hash), [location.hash]);
  const queryTab = (searchParams.get("tab") as Tab | null) ?? null;
  const tab: Tab =
    parsed?.tab ?? (queryTab === TABS.CATEGORIES ? TABS.CATEGORIES : TABS.CONDITIONS);

  const [openConds, setOpenConds] = useState<Set<string>>(new Set());
  const [openCats, setOpenCats] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!parsed) return;
    if (parsed.tab === TABS.CONDITIONS) {
      setOpenConds((prev) => addToSet(prev, parsed.target));
    } else {
      setOpenCats((prev) => addToSet(prev, parsed.target));
    }
    const id =
      parsed.tab === TABS.CONDITIONS ? conditionAnchor(parsed.target) : `cat-${parsed.target}`;
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "center" }));
    }
  }, [parsed]);

  const switchTab = useCallback(
    (next: Tab) => {
      navigate(`${location.pathname}${next === TABS.CATEGORIES ? "?tab=categories" : ""}`, {
        replace: false,
      });
    },
    [navigate, location.pathname],
  );

  const toggleCond = useCallback((code: string) => {
    setOpenConds((prev) => toggleInSet(prev, code));
  }, []);

  const toggleCat = useCallback((slug: string) => {
    setOpenCats((prev) => toggleInSet(prev, slug));
  }, []);

  if (!manifest || !tenant) return null;

  return (
    <>
      <TopBar tenant={tenant} title="Conditions" />
      <div className="app-content">
        <div className="tabs">
          <button
            type="button"
            className={`tab ${tab === TABS.CONDITIONS ? "active" : ""}`}
            onClick={() => switchTab(TABS.CONDITIONS)}
          >
            Conditions
            <span className="tab-count">{tax?.conditions.length ?? "—"}</span>
          </button>
          <button
            type="button"
            className={`tab ${tab === TABS.CATEGORIES ? "active" : ""}`}
            onClick={() => switchTab(TABS.CATEGORIES)}
          >
            Categories
            <span className="tab-count">{tax?.categories.length ?? "—"}</span>
          </button>
        </div>

        {tab === TABS.CONDITIONS ? (
          <ConditionsTab
            tax={tax}
            taxError={taxError}
            openConds={openConds}
            toggleCond={toggleCond}
          />
        ) : (
          <CategoriesTab tax={tax} taxError={taxError} openCats={openCats} toggleCat={toggleCat} />
        )}
      </div>
    </>
  );
}
